// Module-level shared caches and constants extracted from NewTripModal.
//
// `cbpLookupCache` — keyed by ICAO; `null` entry = confirmed "not a CBP
// fact-sheet airport" so we don't refetch.
// `airportCountryCache` — keyed by ICAO; `null` = unknown country.
import api from "@/lib/api";

export const cbpLookupCache = new Map();
// `mxLookupCache` — keyed by ICAO; `null` entry = confirmed "no AIP MX
// customs record" so the MX customs card lookup never refetches.
export const mxLookupCache = new Map();
// `airportMetaCache` — keyed by ICAO; caches the slice of /airports/{icao}
// we actually use ({ country, tz_offset }). `null` = unknown airport.
export const airportMetaCache = new Map();
// In-flight promise cache so concurrent fetchAirportMeta(icao) calls share a
// single network request. Without this, two legs typing the same ICAO at the
// same time both hit /airports/{icao}.
const airportMetaInFlight = new Map();

// USA mainland, Puerto Rico, and US Virgin Islands share customs space —
// flights between them are domestic for passport purposes. Mirrors the set
// used in flight_readiness_engine.py for CBP country acceptance.
export const US_TERRITORY_COUNTRIES = new Set(["USA", "Puerto Rico", "US Virgin Islands"]);
export const normalizeCountry = (c) => (US_TERRITORY_COUNTRIES.has(c) ? "USA" : c || "");

// Fetch the airport metadata we need (country + tz_offset) from the airport
// directory, cached per ICAO. Returns `null` for unknown/invalid codes.
export const fetchAirportMeta = (icao) => {
  const key = (icao || "").trim().toUpperCase();
  if (!/^[A-Z]{4}$/.test(key)) return Promise.resolve(null);
  if (airportMetaCache.has(key)) return Promise.resolve(airportMetaCache.get(key));
  if (airportMetaInFlight.has(key)) return airportMetaInFlight.get(key);
  const p = api
    .get(`/airports/${key}`)
    .then((res) => {
      const meta = res.data
        ? {
            country: res.data.country || null,
            tz_offset: typeof res.data.tz_offset === "number" ? res.data.tz_offset : null,
          }
        : null;
      airportMetaCache.set(key, meta);
      return meta;
    })
    .catch(() => {
      airportMetaCache.set(key, null);
      return null;
    })
    .finally(() => {
      airportMetaInFlight.delete(key);
    });
  airportMetaInFlight.set(key, p);
  return p;
};

export const fetchAirportCountry = (icao) => fetchAirportMeta(icao).then((m) => m?.country || null);

// Coarse "is this a US ICAO code?" test. Used to suppress the CBP info
// card when the flight's origin is also in the US — CBP only processes
// inbound international arrivals, so a domestic leg (e.g. KJFK->KLAX)
// shouldn't surface CBP hours at the destination.
//   K*  = continental US
//   PA* = Alaska        PH* = Hawaii
//   PG* = Guam / NMI    PM* = Midway      PP* = Palmyra
//   PT* = Trust Terr.   PW* = Wake
//   TJ* = Puerto Rico   TI* = US Virgin Islands
export const US_ICAO_RE = /^(K|PA|PH|PG|PM|PP|PT|PW|TJ|TI)[A-Z0-9]/i;
export const isUsIcao = (s) => US_ICAO_RE.test((s || "").trim().toUpperCase());

// Coarse "is this a Mexican ICAO code?" test. Every Mexican aerodrome code
// in the ICAO scheme begins with "MM". Used to surface the AIP MX customs
// card on the Mexican endpoint of an international leg (arrival OR departure)
// and to suppress it on a domestic MX leg where both ends are "MM…".
export const MX_ICAO_RE = /^MM[A-Z0-9]/i;
export const isMxIcao = (s) => MX_ICAO_RE.test((s || "").trim().toUpperCase());

export const FUEL_UNITS = ["lbs", "gal", "liters", "kg"];

// Flight roles for crew members
export const FLIGHT_ROLES = [
  { value: "PIC", label: "PIC", description: "Pilot in Command" },
  { value: "SIC", label: "SIC", description: "Second in Command" },
  { value: "DH", label: "Deadhead", description: "Non-operating crew" },
  { value: "OBS", label: "Observer", description: "Observer" },
];

export const DEFAULT_LEG = {
  origin: "",
  destination: "",
  departure_date: "",
  departure_time: "",
  arrival_date: "",
  arrival_time: "",
  ete_formatted: "",
  tz_diff_hours: null,
  origin_info: null,
  dest_info: null,
  status: "pending",
  time_tbd: false,
  is_international: false,
  is_ferry_flight: false,
  passenger_count: 1,
  passengers: [
    {
      first_name: "",
      middle_name: "",
      last_name: "",
      passport_number: "",
      passport_expiration: "",
      passport_country: "",
      dob: "",
      address: "",
    },
  ],
  same_address_for_all: false,
  crew: [],
  external_crew: [],
  catering_notes: "",
  ground_service_notes: "",
  alternate_airport: "",
  fuel_order: null,
};

export const paxFullName = (pax) => {
  const first = (pax?.first_name || "").trim();
  const last = (pax?.last_name || "").trim();
  return `${first} ${last}`.trim();
};

// True when a passenger row carries ANY captured data. Used to protect filled
// rows from the passenger-count stepper: lowering the count only ever removes
// EMPTY rows, never one the user already typed into. A filled row can only be
// removed via its own trash button (removePassenger).
export const paxHasContent = (pax) =>
  !!(
    (pax?.first_name || "").trim() ||
    (pax?.middle_name || "").trim() ||
    (pax?.last_name || "").trim() ||
    (pax?.passport_number || "").trim() ||
    pax?.passport_expiration ||
    (pax?.passport_country || "").trim() ||
    pax?.dob ||
    (pax?.address || "").trim()
  );

// Join split name parts into a single display string, dropping empties and
// collapsing whitespace. Mirrors backend `combine_person_name`; used to keep
// external-crew `name` consistent with its first/middle/last parts.
export const composeName = (first, middle, last) =>
  [first, middle, last]
    .map((s) => (s || "").trim())
    .filter(Boolean)
    .join(" ");

// Clona campos heredables del leg fuente segun `opts`. Devuelve solo las
// llaves solicitadas (spread-friendly). pax incluye flag ferry porque son
// excluyentes — un leg es ferry o tiene pax, no ambos.
export const clonePeopleFromLeg = (sourceLeg, opts = { pax: true, crew: true }) => {
  if (!sourceLeg) return null;
  const out = {};
  if (opts.pax) {
    const passengers = (sourceLeg.passengers || []).map((p) => ({
      // Conservar el vínculo al pasajero guardado: sin él, el leg heredado
      // se ve "no seleccionado" y la persona reaparece en el dropdown de
      // pasajeros guardados (y en el autocomplete) del tramo nuevo.
      ...(p.passenger_id ? { passenger_id: p.passenger_id } : {}),
      first_name: p.first_name || "",
      middle_name: p.middle_name || "",
      last_name: p.last_name || "",
      passport_number: p.passport_number || "",
      passport_expiration: p.passport_expiration || "",
      passport_country: p.passport_country || "",
      dob: p.dob || "",
      address: p.address || "",
    }));
    out.is_ferry_flight = !!sourceLeg.is_ferry_flight;
    out.passengers = passengers;
    out.passenger_count = passengers.length || (out.is_ferry_flight ? 0 : 1);
  }
  if (opts.crew) {
    // Internal crew: viene en sourceLeg.crew con user_id e is_external=false/undef
    // (mismo shape en memoria y en DB).
    const allCrew = sourceLeg.crew || [];
    out.crew = allCrew.filter((c) => !c.is_external && c.user_id);
    // External crew: en memoria vive en sourceLeg.external_crew como
    // {id,name,flight_role}; en DB (append mode) viene mergeado en
    // sourceLeg.crew con is_external=true. Aceptar ambos shapes para que
    // los externos tambien se hereden al agregar legs dentro del modal.
    const fromDb = allCrew
      .filter((c) => c.is_external)
      .map((c) => ({
        id: c.user_id || `ext-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        first_name: c.first_name || "",
        middle_name: c.middle_name || "",
        last_name: c.last_name || "",
        name: c.name || "",
        phone: c.phone || "",
        email: c.email || "",
        flight_role: c.flight_role || "PIC",
        is_external: true,
      }));
    const fromMem = (sourceLeg.external_crew || []).map((c) => ({
      id: c.id || `ext-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      first_name: c.first_name || "",
      middle_name: c.middle_name || "",
      last_name: c.last_name || "",
      name: c.name || "",
      phone: c.phone || "",
      email: c.email || "",
      flight_role: c.flight_role || "PIC",
      is_external: true,
    }));
    // Dedup por nombre normalizado para evitar duplicados si por algun
    // motivo el sourceLeg tiene ambos shapes poblados.
    const seen = new Set();
    out.external_crew = [...fromMem, ...fromDb].filter((c) => {
      const k = (c.name || "").toLowerCase().trim();
      if (!k) return true;
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });
  }
  return out;
};
